import React, { useRef, lazy, Suspense, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import PerformanceMonitor from '@/components/PerformanceMonitor';
import { Helmet } from 'react-helmet-async';
import { useDirection } from '@/providers/DirectionProvider';
import { initializeProfessionsCacheNew, getProfessionLabelNew, loadProfessionsFromLocalStorage } from '@/services/professions/getProfessionsNew';
import i18n from '@/i18n/config';

// Lazy load non-critical sections
const PopularServicesSection = lazy(() => import('@/components/home/PopularServicesSection'));
const HowItWorksSection = lazy(() => import('@/components/home/HowItWorksSection'));
const TestimonialsSection = lazy(() => import('@/components/home/TestimonialsSection'));
const WhyChooseUsSection = lazy(() => import('@/components/home/WhyChooseUsSection'));
const ArticlesSection = lazy(() => import('@/components/home/ArticlesSection'));
const GeneralQASection = lazy(() => import('@/components/faq/content/GeneralQASection'));
const CtaSection = lazy(() => import('@/components/home/CtaSection'));

// Import critical sections normally
import HeroSection from '@/components/home/HeroSection';
import SearchSection from '@/components/home/SearchSection';

const Index = () => {
  const { t } = useTranslation();
  const { direction } = useDirection();
  const requestFormRef = useRef<HTMLDivElement>(null);
  const searchSectionRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Read profession from URL parameter for dynamic landing pages
  const urlParams = new URLSearchParams(location.search);
  const professionFromUrl = urlParams.get('profession');
  
  // Pre-load profession label to prevent flash
  const [professionLabel, setProfessionLabel] = useState<string | null>(null);
  const [isLoadingProfession, setIsLoadingProfession] = useState(!!professionFromUrl);

  useEffect(() => {
    if (professionFromUrl) {
      setIsLoadingProfession(true);
      
      // Try localStorage first (instant) - already loaded in main.tsx but re-check
      const loadedFromLocal = loadProfessionsFromLocalStorage();
      if (loadedFromLocal) {
        const label = getProfessionLabelNew(professionFromUrl, i18n.language);
        if (label && label !== professionFromUrl) {
          setProfessionLabel(label);
          setIsLoadingProfession(false);
        }
      }
      
      // Always refresh from DB (background or blocking if not loaded)
      const loadProfession = async () => {
        await initializeProfessionsCacheNew();
        const label = getProfessionLabelNew(professionFromUrl, i18n.language);
        if (label && label !== professionFromUrl) {
          setProfessionLabel(label);
        }
        setIsLoadingProfession(false);
      };
      
      if (!loadedFromLocal) {
        loadProfession();
      } else {
        // Background refresh - don't await
        initializeProfessionsCacheNew();
      }
    } else {
      setProfessionLabel(null);
      setIsLoadingProfession(false);
    }
  }, [professionFromUrl]);

  const scrollToRequestForm = () => {
    requestFormRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };

  const scrollToSearchSection = () => {
    searchSectionRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };

  const handleSearchSubmit = (profession: string, location: string) => {
    navigate(`/search?profession=${profession}&location=${location}`);
  };

  // Loading fallback component
  const SectionLoader = () => (
    <div className="py-16 animate-pulse">
      <div className="container mx-auto px-6">
        <div className="h-8 bg-gray-200 rounded w-1/3 mx-auto mb-8"></div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-32 bg-gray-200 rounded-lg"></div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col min-h-screen overflow-y-auto" dir={direction}>
      <PerformanceMonitor />

      <Helmet>
        {/* Enhanced SEO and performance meta tags */}
        <title>{t('seo.homeTitle')}</title>
        <meta name="description" content={t('seo.homeDescription')} />
        <meta name="keywords" content={t('seo.homeKeywords')} />

        {/* Open Graph optimized */}
        <meta property="og:title" content={t('seo.homeTitle')} />
        <meta property="og:description" content={t('seo.homeDescription')} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://ofair.co.il" />
        <meta property="og:image" content="https://ofair.co.il/logo-new.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="he_IL" />
        <meta property="og:site_name" content="oFair - עופר" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t('seo.homeTitle')} />
        <meta name="twitter:description" content={t('seo.homeDescription')} />
        <meta name="twitter:image" content="https://ofair.co.il/logo-new.png" />

        {/* hreflang tags for multi-language support */}
        <link rel="alternate" hrefLang="he" href="https://ofair.co.il" />
        <link rel="alternate" hrefLang="en" href="https://ofair.co.il?lang=en" />
        <link rel="alternate" hrefLang="ru" href="https://ofair.co.il?lang=ru" />
        <link rel="alternate" hrefLang="fr" href="https://ofair.co.il?lang=fr" />
        <link rel="alternate" hrefLang="x-default" href="https://ofair.co.il" />

        {/* Structured Data - Organization + WebSite + LocalBusiness */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Organization",
                "@id": "https://ofair.co.il/#organization",
                "name": "oFair - עופר",
                "alternateName": ["עופר", "oFair", "אופייר"],
                "url": "https://ofair.co.il",
                "logo": {
                  "@type": "ImageObject",
                  "url": "https://ofair.co.il/logo-new.png"
                },
                "description": t('seo.homeDescription'),
                "sameAs": [],
                "contactPoint": {
                  "@type": "ContactPoint",
                  "telephone": "+972-50-5577565",
                  "contactType": "customer service",
                  "email": "contact@ofair.co.il",
                  "areaServed": "IL",
                  "availableLanguage": ["Hebrew", "English", "Russian", "French"]
                }
              },
              {
                "@type": "WebSite",
                "@id": "https://ofair.co.il/#website",
                "url": "https://ofair.co.il",
                "name": "oFair - עופר",
                "alternateName": ["עופר", "oFair", "אופייר"],
                "description": t('seo.homeDescription'),
                "publisher": {
                  "@id": "https://ofair.co.il/#organization"
                },
                "inLanguage": "he-IL",
                "potentialAction": {
                  "@type": "SearchAction",
                  "target": {
                    "@type": "EntryPoint",
                    "urlTemplate": "https://ofair.co.il/search?profession={search_term_string}"
                  },
                  "query-input": "required name=search_term_string"
                }
              },
              {
                "@type": "LocalBusiness",
                "@id": "https://ofair.co.il/#localbusiness",
                "name": "oFair - עופר",
                "alternateName": "עופר",
                "description": t('seo.homeDescription'),
                "url": "https://ofair.co.il",
                "telephone": "+972-50-5577565",
                "email": "contact@ofair.co.il",
                "address": {
                  "@type": "PostalAddress",
                  "addressCountry": "IL",
                  "addressLocality": "ישראל"
                },
                "areaServed": {
                  "@type": "Country",
                  "name": "Israel"
                },
                "priceRange": "Free",
                "serviceType": "Service Provider Marketplace"
              }
            ]
          })}
        </script>

        <link rel="canonical" href="https://ofair.co.il" />

        {/* Performance hints */}
        <link rel="prefetch" href="/search" />
        <link rel="prefetch" href="/dashboard" />
      </Helmet>

      <Header />

      {/* Hero Section - Critical, load immediately */}
      <div ref={requestFormRef}>
        <HeroSection
          scrollToRequestForm={scrollToRequestForm}
          scrollToSearchSection={scrollToSearchSection}
          initialProfession={professionFromUrl}
          professionLabel={professionLabel}
          isLoadingProfession={isLoadingProfession}
        />
      </div>

      {/* Popular Services Section - Lazy loaded */}
      <Suspense fallback={<SectionLoader />}>
        <PopularServicesSection />
      </Suspense>

      {/* Search Section */}
      <div id="search-section" ref={searchSectionRef}>
        <SearchSection onSearchSubmit={handleSearchSubmit} />
      </div>

      {/* Below-the-fold sections - Lazy loaded */}
      <Suspense fallback={<SectionLoader />}>
        <HowItWorksSection />
      </Suspense>

      <Suspense fallback={<SectionLoader />}>
        <TestimonialsSection />
      </Suspense>

      <Suspense fallback={<SectionLoader />}>
        <WhyChooseUsSection />
      </Suspense>

      <Suspense fallback={<SectionLoader />}>
        <ArticlesSection />
      </Suspense>

      <Suspense fallback={<SectionLoader />}>
        <GeneralQASection />
      </Suspense>

      <Suspense fallback={<SectionLoader />}>
        <CtaSection scrollToRequestForm={scrollToRequestForm} />
      </Suspense>

      <Footer />
    </div>
  );
};

export default Index;
